// ─── What: the trade scatter ──────────────────────────────────────────────────
//
// Two sources feed one overlay: the highlighted traders' trades (always on,
// always individual) and — behind the Trades chip — every trade in the window.
// The second can be thousands of rows, so it is clustered; the first never is,
// because a trader you singled out is exactly what you came to look at.

import * as d3 from 'd3';
import type React from 'react';
import {
    clusterTrades,
    type ClusterInput,
    type TradeCluster,
} from '../../tradeClustering';
import { clampToDomain, offScaleDirection } from '../../chartYDomain';
import { tradeKey } from '../../tradePlate';
import {
    placeTooltip,
    buildClusterTooltipHtml,
    buildTradeTooltipHtml,
    type TooltipColors,
} from '../../chartTooltips';
import type { TradeOverlayData } from '../../chartTypes';
import { MARGIN } from '../layout';
import { MARK_R_MIN, MARK_R_MAX } from '../constants';
import type { ChartGroup, DrawCtx } from '../drawContext';

/** Below this radius a count would not fit inside the triangle. */
const CLUSTER_LABEL_MIN_R = 6.5;

export interface OverlayStats {
    trades: number;
    marks: number;
    clustered: boolean;
}

export interface TradeOverlayDeps {
    tradeOverlay?: TradeOverlayData;
    windowTrades?: TradeOverlayData['trades'];
    showWindowTrades: boolean;
    /** Read, not depended on, so hover does not rebuild the chart. */
    hoveredTradeKeyRef: React.MutableRefObject<string | null | undefined>;
    /** Rebuilt each pass: trade key -> the mark drawn for it. */
    tradeMarkIndexRef: React.MutableRefObject<Map<string, SVGPathElement>>;
    setOverlayStats: React.Dispatch<React.SetStateAction<OverlayStats | null>>;
    onTradeHover?: (key: string | null) => void;
}

/**
 * One identity for a trade, used to merge the two sources and then to address the
 * mark drawn for it. tradeKey() is field-derived, so two distinct fills that
 * agree on every field collide under it — merging on that would silently drop
 * one, and indexing on it would let one overwrite the other's mark.
 */
function seenKey(t: TradeOverlayData['trades'][number]): string {
    return t.id ? `id:${t.id}` : tradeKey(t);
}

function trianglePath(
    x: number,
    y: number,
    size: number,
    side: 'buy' | 'sell'
): string {
    return side === 'buy'
        ? `M${x},${y - size} L${x - size},${y + size} L${x + size},${y + size} Z`
        : `M${x},${y + size} L${x - size},${y - size} L${x + size},${y - size} Z`;
}

/**
 * One mark standing for several trades. It keeps the buy/sell triangle so it
 * reads in the same language as a single trade, and adds a ring so it is never
 * mistaken for one. Big enough clusters carry their count.
 */
function drawCluster(
    group: ChartGroup,
    cluster: TradeCluster,
    size: number,
    color: string,
    tooltipRef: React.RefObject<HTMLDivElement>,
    tc: TooltipColors,
    index: Map<string, SVGPathElement>,
    hoveredKey?: string | null
): void {
    const { x, y } = cluster;

    group
        .append('circle')
        .attr('class', 'trade-cluster-ring')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', size + 2.5)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-opacity', 0.5)
        .attr('stroke-width', 1)
        .style('pointer-events', 'none');

    const path = group.append('path');
    // Every member resolves to this mark, so hovering any of their rows in the
    // list lights up the cluster that swallowed it.
    for (const key of cluster.keys) index.set(key, path.node()!);

    // Re-applied here rather than left to the hover effect, so emphasis survives
    // a full chart rebuild the way a single trade's does.
    const isHovered = hoveredKey != null && cluster.keys.includes(hoveredKey);
    path.attr('class', 'trade-marker trade-marker--cluster')
        .attr('data-cluster-count', String(cluster.count))
        .attr('data-base-fillop', 0.45)
        .attr('data-base-strokeop', 0.8)
        .attr('d', trianglePath(x, y, size, cluster.side))
        .attr('fill', color)
        .attr('fill-opacity', isHovered ? 0.95 : 0.45)
        .attr('stroke', color)
        .attr('stroke-opacity', isHovered ? 1 : 0.8)
        .attr('stroke-width', isHovered ? 1.5 : 0.5)
        .style('cursor', 'pointer');

    // The radius scale tops out at MARK_R_MAX, so this threshold has to sit
    // inside that range or the label is unreachable.
    if (size >= CLUSTER_LABEL_MIN_R) {
        group
            .append('text')
            .attr('class', 'trade-cluster-count')
            .attr('x', x)
            .attr('y', y + (cluster.side === 'buy' ? size * 0.55 : -size * 0.2))
            .attr('text-anchor', 'middle')
            .attr('font-size', '7px')
            .attr('font-family', 'monospace')
            .attr('font-weight', 700)
            .attr('fill', tc.textColor)
            .style('pointer-events', 'none')
            .text(cluster.count);
    }

    path.on('mouseenter', (event: MouseEvent) => {
        placeTooltip(
            tooltipRef.current,
            event,
            buildClusterTooltipHtml(cluster, cluster.avgPrice ?? 0, color, tc)
        );
    }).on('mouseleave', () => {
        if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    });
}

export function drawTradeOverlay(ctx: DrawCtx, deps: TradeOverlayDeps): void {
    const {
        svg,
        layout,
        colors,
        theme,
        xScale,
        yScale,
        probDomain,
        selectedIds,
        chartType,
        outcomesMap,
        showNoLine,
        tooltipRef,
    } = ctx;
    const {
        tradeOverlay,
        windowTrades,
        showWindowTrades,
        hoveredTradeKeyRef,
        tradeMarkIndexRef,
        setOverlayStats,
        onTradeHover,
    } = deps;
    const { w } = layout;
    const { tc } = colors;

    tradeMarkIndexRef.current = new Map();
    const highlightedAccts =
        tradeOverlay?.highlightedAccounts ?? new Set<string>();
    const acctColorMap =
        tradeOverlay?.accountColorMap ?? new Map<string, string>();

    const overlayTrades: TradeOverlayData['trades'] = [];
    {
        const seen = new Set<string>();
        const push = (list?: TradeOverlayData['trades']) => {
            for (const t of list ?? []) {
                const k = seenKey(t);
                if (seen.has(k)) continue;
                seen.add(k);
                overlayTrades.push(t);
            }
        };
        // Highlighted traders first, so a trade held by both sources keeps the
        // identity that carries its account colour.
        push(tradeOverlay?.trades);
        if (showWindowTrades) push(windowTrades);
    }

    if (overlayTrades.length === 0) {
        setOverlayStats(null);
        return;
    }

    const tradeGroup = svg
        .append('g')
        .attr('clip-path', `url(#${layout.clipIds.prob})`) as ChartGroup;
    const hasAccountFilter = highlightedAccts.size > 0;

    // A NO trade belongs on the NO mirror line at its raw price when that line is
    // drawn for its market, and on the YES scale (1 - price) otherwise. Decided
    // per market, not once for the chart: with several markets drawn there is no
    // mirror line at all, so an unflipped NO fill at 0.30 would land wherever
    // some other candidate's YES line happens to sit.
    const noLineDrawnFor = (marketId: string) =>
        chartType === 'line' &&
        showNoLine === marketId &&
        selectedIds.includes(marketId);

    // Place every trade first, then decide what survives as its own mark —
    // clustering is a screen-space question, so it needs the pixel positions.
    interface PlacedTrade {
        trade: TradeOverlayData['trades'][number];
        displayPrice: number;
        isFlipped: boolean;
        offScale: ReturnType<typeof offScaleDirection>;
    }
    const placed = new Map<string, PlacedTrade>();
    const clusterInput: ClusterInput[] = [];

    for (const trade of overlayTrades) {
        // A trade whose market is not drawn has no line to sit on.
        if (!selectedIds.includes(trade.marketId)) continue;
        const tx = xScale(trade.timestamp);
        if (tx < MARGIN.left || tx > MARGIN.left + w) continue;
        const isFlipped =
            trade.outcomeIndex === 1 && !noLineDrawnFor(trade.marketId);
        const displayPrice = isFlipped ? 1 - trade.price : trade.price;
        // A trade outside the fitted axis is pinned to the edge, so it must not
        // read as a trade that happened AT the edge.
        const offScale = offScaleDirection(displayPrice, probDomain.domain);
        const ty = yScale(clampToDomain(displayPrice, probDomain.domain));
        const key = seenKey(trade);
        placed.set(key, { trade, displayPrice, isFlipped, offScale });
        clusterInput.push({
            key,
            x: tx,
            y: ty,
            quoteAmount: trade.quoteAmount,
            side: trade.side,
            pinned: hasAccountFilter && highlightedAccts.has(trade.account),
            ms: trade.timestamp.getTime(),
            price: displayPrice,
        });
    }

    const clusters = clusterTrades(clusterInput);
    const clustered = clusters.some(c => c.count > 1);
    const marksShown = clusters.length;
    const tradesPlaced = clusterInput.length;
    // Identity-preserving update: this runs inside the render effect, so a fresh
    // object every pass would loop.
    setOverlayStats(prev =>
        prev &&
        prev.trades === tradesPlaced &&
        prev.marks === marksShown &&
        prev.clustered === clustered
            ? prev
            : { trades: tradesPlaced, marks: marksShown, clustered }
    );

    // Size against the largest mark, cluster or not, so a cluster of small trades
    // cannot outgrow a genuine whale beside it.
    const maxVol = d3.max(clusters, c => c.totalQuote) ?? 1;
    const tradeRScale = d3
        .scaleSqrt()
        .domain([0, maxVol])
        .range([MARK_R_MIN, MARK_R_MAX]);

    for (const cluster of clusters) {
        if (cluster.count > 1) {
            drawCluster(
                tradeGroup,
                cluster,
                tradeRScale(cluster.totalQuote),
                cluster.side === 'buy'
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                tooltipRef,
                tc,
                tradeMarkIndexRef.current,
                hoveredTradeKeyRef.current
            );
            continue;
        }

        const rec = placed.get(cluster.key!)!;
        const { trade, displayPrice, isFlipped, offScale } = rec;
        const tx = cluster.x;
        const ty = cluster.y;

        const isHighlighted =
            hasAccountFilter && highlightedAccts.has(trade.account);
        const isDimmed = hasAccountFilter && !isHighlighted;
        const acctColor = acctColorMap.get(trade.account);
        const dotColor =
            acctColor && (isHighlighted || !hasAccountFilter)
                ? acctColor
                : trade.side === 'buy'
                  ? theme.palette.success.main
                  : theme.palette.error.main;

        const key = cluster.key!;
        // The list hovers by tradeKey(), so a singleton still exposes that as its
        // data attribute even when it is indexed by row id.
        const hoverKey = tradeKey(trade);
        const isTradeHovered = hoveredTradeKeyRef.current === hoverKey;

        const size = tradeRScale(trade.quoteAmount);
        const baseFillOp = isDimmed ? 0.08 : 0.5;
        const baseStrokeOp = isDimmed ? 0.12 : 0.7;

        if (offScale) {
            // Caret past the marker, pointing where the trade really is.
            const dir = offScale === 'above' ? -1 : 1;
            const cy = ty + dir * (size + 3);
            tradeGroup
                .append('path')
                .attr('class', 'trade-offscale-caret')
                .attr(
                    'd',
                    `M${tx - size},${cy} L${tx},${cy + dir * size * 0.8} L${tx + size},${cy}`
                )
                .attr('fill', 'none')
                .attr('stroke', dotColor)
                .attr('stroke-opacity', baseStrokeOp)
                .attr('stroke-width', 1.2)
                .attr('stroke-linecap', 'round')
                .style('pointer-events', 'none');
        }

        const markerSel = tradeGroup.append('path');
        tradeMarkIndexRef.current.set(key, markerSel.node()!);
        tradeMarkIndexRef.current.set(hoverKey, markerSel.node()!);
        markerSel
            .attr(
                'class',
                offScale
                    ? 'trade-marker trade-marker--offscale'
                    : 'trade-marker'
            )
            .attr('data-trade-key', hoverKey)
            .attr('data-base-fillop', offScale ? 0 : baseFillOp)
            .attr('data-base-strokeop', baseStrokeOp)
            .attr('d', trianglePath(tx, ty, size, trade.side))
            .attr('fill', offScale ? 'none' : dotColor)
            .attr(
                'fill-opacity',
                offScale ? 0 : isTradeHovered ? 0.95 : baseFillOp
            )
            .attr('stroke', dotColor)
            .attr('stroke-opacity', isTradeHovered ? 1 : baseStrokeOp)
            .attr('stroke-width', isTradeHovered ? 1.5 : 0.5)
            .style('cursor', 'pointer')
            .on('mouseenter', (event: MouseEvent) => {
                onTradeHover?.(key);
                placeTooltip(
                    tooltipRef.current,
                    event,
                    buildTradeTooltipHtml(
                        trade,
                        dotColor,
                        theme.palette.success.main,
                        theme.palette.error.main,
                        tc,
                        isFlipped ? displayPrice : null,
                        outcomesMap.get(trade.marketId) ?? null
                    )
                );
            })
            .on('mouseleave', () => {
                onTradeHover?.(null);
                if (tooltipRef.current)
                    tooltipRef.current.style.display = 'none';
            });
    }
}
